import React, { useMemo } from 'react';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import type { ItemsConfig } from '@atlassian/global-side-navigation/types';
import type { CustomItemProps } from '@atlassian/navigation-common/types';
import { Nav4Filters } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-filters/src/index.tsx';
import {
	L1_MENU_ID,
	type JiraConfigurableNavigationItem,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { Nav4Apps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-apps/src/index.tsx';
import { Nav4Assets } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-assets/src/ui/index.tsx';
import { CustomerService } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-customer-service/src/index.tsx';
import { Nav4Dashboards } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-dashboards/src/index.tsx';
import { Nav4Operations } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-operations/src/ui/Nav4Operations.tsx';
import { Nav4Overviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/index.tsx';
import { Nav4Plans } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-plans/src/index.tsx';
import { Nav4Projects } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-core/src/index.tsx';
import { Nav4Roadmaps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-roadmaps/src/index.tsx';
import { Teams } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Stub } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/stub/main.tsx';

import { isTeamsAppEnabled } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { getConfigurableMessageAndIcon } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { isConfigurableMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/is-configurable-menu-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';

import type { GSNForContentSidebarQuery$data } from '../../ui/__generated__/GSNForContentSidebarQuery.graphql';

type ComponentProps = {
	queryRef: GSNForContentSidebarQuery$data;
	isExpanded: boolean;
};

export function useItemsConfig(
	navItems: JiraConfigurableNavigationItem[],
	queryRef: GSNForContentSidebarQuery$data,
	shouldRenderOverviews: boolean,
): ItemsConfig {
	const { formatMessage } = useIntl();

	const components = useMemo(() => {
		const componentProps: ComponentProps = { isExpanded: false, queryRef };
		const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

		return navItems.map(({ menuId }) => {
			switch (menuId) {
				case L1_MENU_ID.APPS:
					return <Nav4Apps {...componentProps} />;
				case L1_MENU_ID.ASSETS:
					/* remove this case on
					 * assets_as_an_app_v2 cleanup
					 */
					return <Nav4Assets isExpanded={componentProps.isExpanded} />;
				case L1_MENU_ID.DASHBOARDS:
					return <Nav4Dashboards isExpanded={componentProps.isExpanded} />;
				case L1_MENU_ID.FILTERS:
					return <Nav4Filters {...componentProps} />;
				case L1_MENU_ID.GOALS:
					return (
						<Stub menuId={L1_MENU_ID.GOALS} href={USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GOALS]} />
					);
				case L1_MENU_ID.OPERATIONS:
					return <Nav4Operations {...componentProps} />;
				case L1_MENU_ID.PLANS:
					return <Nav4Plans {...componentProps} />;
				case L1_MENU_ID.OVERVIEWS:
					return shouldRenderOverviews ? <Nav4Overviews {...componentProps} /> : null;
				case L1_MENU_ID.PROJECTS:
					return <Nav4Projects {...componentProps} />;
				case L1_MENU_ID.ROADMAPS:
					return <Nav4Roadmaps isExpanded={componentProps.isExpanded} />;
				case L1_MENU_ID.TEAMS:
					return !isTeamsAppEnabled() ? <Teams /> : null;
				case L1_MENU_ID.CUSTOMER_SERVICE:
					return fg('jcs_master_flag') ? <CustomerService /> : null;
				default:
					return null;
			}
		});
	}, [navItems, queryRef, shouldRenderOverviews]);

	return useMemo(
		() =>
			navItems.reduce<ItemsConfig>((acc, navItem, index) => {
				const component = components[index];

				// We need a configurable menu id and a component to continue
				if (isConfigurableMenuId(navItem.menuId) && component) {
					const { message, Icon } = getConfigurableMessageAndIcon(navItem.menuId);
					const label = formatMessage(message);

					// See src/packages/navigation-apps/sidebar-nav4/sidebars/common/core/src/common/utils/use-formatted-message-and-icon/index.tsx
					const elemBefore = <Icon label={label} spacing="spacious" color="currentColor" />;

					acc[navItem.menuId] = {
						type: 'custom',
						elemBefore,
						label,
						isMenuListItem: true, // all of these menu items are already list-items. No need to wrap them in a list-item
						Component: ({ isInMoreMenu, onClick, onPeek }: CustomItemProps) => {
							// When the item isInMoreMenu, we cannot render directly as this breaks the Peek logic.
							if (isInMoreMenu) {
								// This is the same transformation happening inside GSN
								const handleClick = () => {
									onPeek?.();
									onClick?.();
								};

								// todo: We should handle this internally in GSN to avoid duplicating this logic
								return (
									<ButtonMenuItem onClick={handleClick} elemBefore={elemBefore}>
										{label}
									</ButtonMenuItem>
								);
							}
							return component;
						},
					};
				}

				return acc;
			}, {}),
		[components, formatMessage, navItems],
	);
}
